import { Platform, View, type ViewProps } from 'react-native';
import RNCSlider from '@react-native-community/slider';
import { cn } from '@/lib/utils';
import { useColor } from '~/global/hooks/useColor';

type SliderProps = Pick<ViewProps, 'className'> & {
  value: number;
  onValueChange: (value: number) => void;
  onSlidingComplete?: (value: number) => void;
  minimumValue?: number;
  maximumValue?: number;
  step?: number;
  disabled?: boolean;
};

/**
 * Themed value slider. Wraps `@react-native-community/slider` and bridges the
 * design tokens (accent / surface) to its color props, since the underlying
 * component is styled via props rather than `className`.
 */
const Slider = ({
  className,
  value,
  onValueChange,
  onSlidingComplete,
  minimumValue = 0,
  maximumValue = 100,
  step = 1,
  disabled,
}: SliderProps) => {
  const accent = useColor('accent');
  const track = useColor('border');
  const thumbBorder = useColor('background');

  // `thumbStyle` is a web-only prop (absent from the native typings) that lets
  // us give the knob a border so it stands out from the accent track. Typed as
  // `object` so spreading it adds no typed props on native.
  const webThumbProps: object =
    Platform.OS === 'web'
      ? { thumbStyle: { borderWidth: 2, borderColor: thumbBorder } }
      : {};

  return (
    <View className={cn('w-full', className)}>
      <RNCSlider
        style={{ width: '100%', height: 40 }}
        value={value}
        onValueChange={onValueChange}
        onSlidingComplete={onSlidingComplete}
        minimumValue={minimumValue}
        maximumValue={maximumValue}
        step={step}
        disabled={disabled}
        minimumTrackTintColor={accent}
        maximumTrackTintColor={track}
        thumbTintColor={accent}
        {...webThumbProps}
      />
    </View>
  );
};

Slider.displayName = 'Slider';

export { Slider };
export type { SliderProps };
